import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, X } from 'lucide-react';
import { Button, Drawer, FormField, Input, Textarea } from '@ntconsult/aluma-react';
import { useUpdateFile } from '../../hooks/useUpdateFile';
import type { File } from '../../types';

interface EditMetadataDrawerProps {
  file: File;
  open: boolean;
  onClose: () => void;
}

export function EditMetadataDrawer({ file, open, onClose }: EditMetadataDrawerProps) {
  const { t } = useTranslation('video');
  const { mutate: updateFile, isPending } = useUpdateFile(file.id);

  const [description, setDescription] = useState('');
  const [tagsText, setTagsText] = useState('');

  useEffect(() => {
    if (open) {
      setDescription(file.description || '');
      setTagsText(file.tags?.join(', ') || '');
    }
  }, [open, file.description, file.tags]);

  const handleSave = () => {
    const tags = tagsText
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);
    updateFile(
      { description, tags },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      side="right"
      size="md"
      ariaLabel={t('edit.drawerTitle')}
    >
      <Drawer.Header
        title={t('edit.drawerTitle')}
        subtitle={t('edit.drawerSubtitle')}
        onClose={onClose}
      />
      <Drawer.Body>
        <div className="space-y-4">
          <FormField label={t('info.description')}>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('edit.descriptionPlaceholder')}
              rows={6}
              resize="vertical"
            />
          </FormField>
          <FormField label={t('info.tags')}>
            <Input
              type="text"
              value={tagsText}
              onChange={(e) => setTagsText(e.target.value)}
              placeholder={t('edit.tagsPlaceholder')}
            />
          </FormField>
        </div>
      </Drawer.Body>
      <Drawer.Footer>
        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            onClick={onClose}
            leftIcon={<X className="w-4 h-4" aria-hidden="true" />}
          >
            {t('edit.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            loading={isPending}
            leftIcon={<Check className="w-4 h-4" aria-hidden="true" />}
          >
            {t('edit.save')}
          </Button>
        </div>
      </Drawer.Footer>
    </Drawer>
  );
}
